import { gql, useQuery } from '@apollo/client';
import React from 'react';

interface Props {
  userId: string;
}

interface PosthogInfo {
  user: {
    id: string;

    posthog: {
      deviceBrand: string | null;
      deviceManufacturer: string | null;
      deviceModelId: string | null;
      deviceModelName: string | null;
      deviceName: string | null;
      deviceTotalMemoryGB: number | null;
      deviceType: string | null;
      deviceYearClass: string | null;
      installationTime: string | null;
      lastLoginAt: string | null;
      os: string;
      osVersion: string;
      version: string | null;
    } | null;
  };
}

const POSTHOG_INFO_QUERY = gql`
  query PosthogInfo($userId: ID!) {
    user(id: $userId) {
      id

      posthog {
        deviceBrand
        deviceManufacturer
        deviceModelId
        deviceModelName
        deviceName
        deviceTotalMemoryGB
        deviceType
        deviceYearClass
        installationTime
        lastLoginAt
        os
        osVersion
        version
      }
    }
  }
`;

const PosthogInfo = ({ userId }: Props) => {
  const { data, loading } = useQuery<PosthogInfo>(POSTHOG_INFO_QUERY, {
    variables: { userId }
  });

  if (loading || !data?.user?.posthog) {
    return null;
  }

  return (
    <div className="flex w-full flex-col gap-6 overflow-auto bg-base-100 p-6">
      <div className="w-full overflow-x-auto">
        <div className="mb-4 text-xl font-bold">Medyx Native</div>
        <div className="inline-block min-w-full align-middle">
          <div className="overflow-hidden rounded-lg border border-base-300 shadow-sm">
            <table className="min-w-full divide-y divide-base-300">
              <thead className="bg-base-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-base-content">
                    Dernière connexion
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-base-content">
                    Type d'appareil
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-base-content">
                    Système d'exploitation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-base-content">
                    Version de l'application
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-base-content">
                    Date d'installation
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-base-300 bg-base-100">
                <tr className="transition-colors hover:bg-base-200">
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-base-content">
                    {data.user.posthog.lastLoginAt || '-'}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-base-content">
                    {data.user.posthog.deviceType || '-'}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-base-content">
                    {`${data.user.posthog.os} ${data.user.posthog.osVersion}` || '-'}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-base-content">
                    {data.user.posthog.version || '-'}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-base-content">
                    {data.user.posthog.installationTime || '-'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="w-full overflow-x-auto">
        <div className="mb-4 text-xl font-bold">Appareil</div>
        <div className="inline-block min-w-full align-middle">
          <div className="overflow-hidden rounded-lg border border-base-300 shadow-sm">
            <table className="min-w-full divide-y divide-base-300">
              <thead className="bg-base-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-base-content">
                    Nom du modèle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-base-content">
                    Marque
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-base-content">
                    Fabricant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-base-content">
                    ID du modèle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-base-content">
                    Nom de l'appareil
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-base-content">
                    Mémoire totale (GB)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-base-content">
                    Année haut de gamme
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-base-300 bg-base-100">
                <tr className="transition-colors hover:bg-base-200">
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-base-content">
                    {data.user.posthog.deviceModelName || '-'}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-base-content">
                    {data.user.posthog.deviceBrand || '-'}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-base-content">
                    {data.user.posthog.deviceManufacturer || '-'}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-base-content">
                    {data.user.posthog.deviceModelId || '-'}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-base-content">
                    {data.user.posthog.deviceName || '-'}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-base-content">
                    {data.user.posthog.deviceTotalMemoryGB || '-'}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-base-content">
                    {data.user.posthog.deviceYearClass || '-'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PosthogInfo;
